import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useRouter } from 'next/router';

import { FEDERATED_USER_PROVIDERS, IUser, UserContext } from '../../contexts/user/UserContext';
import SuccessAlert from '../../lib/alerts/SuccessAlert';
import { FormProvider, useForm } from 'react-hook-form';
import { validateEmailAddress } from '../../lib/validations/validations';
import { amazonIcon, appleIcon, facebookIcon, googleIcon } from '../../lib/svg-icons/SvgIcons';
import Link from 'next/link';
import { FieldTypes } from '../../lib/form/FormBuilder';
import InputLabelWithValidation from '../../lib/validations/InputLabelWithValidation';
import { federatedSigninPath } from '../../lib/api/Api';
import { updateStorageData } from '../../contexts/storage/LocalStorageProvider';
import { LocalStorageContext } from '../../contexts/storage/LocalStorageContext';

interface ISigninProps {
    isModal?: boolean;
    onSubmit?: (data: any) => void;
    userData?: IUser | null;
}

const SigninOptions: React.FC<ISigninProps> = ({isModal, onSubmit, userData}) => {
    const router = useRouter();
    const query = router.query as { message: string; currentEmail: string };
    const pathname = router.pathname;
    const {redirectUrl} = useContext(UserContext);
    const {purchaseIntentData} = useContext(LocalStorageContext);
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);

    const methods = useForm({
        shouldUseNativeValidation: false,
        mode: 'onBlur',
        reValidateMode: 'onChange',
        resolver: undefined,
        context: undefined,
        criteriaMode: 'all',
        shouldFocusError: true,
        shouldUnregister: true,
        delayError: 100,
        defaultValues: {
            email: currentEmail || userData?.email || ''
        }
    });

    useEffect(() => {
        if (query?.currentEmail) {
            setCurrentEmail(decodeURIComponent(query?.currentEmail));
            const params = new URLSearchParams(query);
            params.delete('currentEmail');
            router.replace(
                {pathname, query: params.toString()},
                undefined,
                {shallow: true}
            );
        }
    }, [query?.currentEmail]);

    const onSubmitHandeler = (data: any) => {
        onSubmit?.(data);
    };

    const handleFederatedSignin = (provider: FEDERATED_USER_PROVIDERS) => {
        if (isModal) {
            updateStorageData('rdRedirectUrl', `${location.pathname}${location.search}`);
        } else if (redirectUrl) {
            updateStorageData('rdRedirectUrl', redirectUrl);
        }

        window.location.href = federatedSigninPath(provider);
    };

    useEffect(() => {
        if (currentEmail) {
            methods.setValue('email', currentEmail);
        }
    }, [currentEmail]);
    const emailRegister = methods.register('email', {
        required: true,
        validate: {
            invalidEmail: (input: string) => validateEmailAddress(input)
        }
    });

    return (
        <div>
            <p className='text-center my-4'>Welcome to PURR! Buy, sell, make offers, list vehicles, book test drives, and more—all in one place. For Buyers, Sellers, and Dealers—The used car marketplace where everybody wins!</p>
            <div className={!isModal ? 'card' : ''}>
                <div className={`p-4`}>
                    {query?.message === 'reset_password' && (
                        <div className="mb-4">
                            <SuccessAlert message="Your password has been reset!"/>
                        </div>
                    )}
                    <h2 className="title mb-3 text-center">Sign in or register</h2>
                    <Button
                        className="destructiveButton w-100 mb-3 py-4"
                        type="button"
                        onClick={() => {
                            handleFederatedSignin(FEDERATED_USER_PROVIDERS.APPLE);
                        }}
                        disabled={methods.formState.isSubmitting}>
                        <div className="social-signin-button">
                            <span className="icon">{appleIcon}</span>
                            <span>Sign in with Apple</span>
                        </div>
                    </Button>

                    <Button
                        className="destructiveButton w-100 mb-3 py-4"
                        type="button"
                        onClick={() => {
                            handleFederatedSignin(FEDERATED_USER_PROVIDERS.GOOGLE);
                        }}
                        disabled={methods.formState.isSubmitting}>
                        <div className="social-signin-button">
                            <span className="icon">{googleIcon}</span>
                            <span>Sign in with Google</span>
                        </div>
                    </Button>

                    <Button
                        className="destructiveButton w-100 mb-3 py-4"
                        type="button"
                        onClick={() => {
                            handleFederatedSignin(FEDERATED_USER_PROVIDERS.AMAZON);
                        }}
                        disabled={methods.formState.isSubmitting}>
                        <div className="social-signin-button">
                            <span className="icon">{amazonIcon}</span>
                            <span>Sign in with Amazon</span>
                        </div>
                    </Button>

                    <Button
                        className="destructiveButton w-100 mb-3 py-4"
                        type="button"
                        onClick={() => {
                            handleFederatedSignin(FEDERATED_USER_PROVIDERS.FACEBOOK);
                        }}
                        disabled={methods.formState.isSubmitting}>
                        <div className="social-signin-button">
                            <span className="icon">{facebookIcon}</span>
                            <span>Sign in with Facebook</span>
                        </div>
                    </Button>


                    <div className="d-flex justify-content-between align-items-center my-4">
                        <span className="line"/>
                        <span className="mx-3">or use your email address</span>
                        <span className="line"/>
                    </div>

                    <FormProvider {...methods}>
                        <Form onSubmit={methods.handleSubmit(onSubmitHandeler as any)}>
                            <Form.Group>
                                <InputLabelWithValidation
                                    label="Email address"
                                    error={methods?.formState?.errors?.email}
                                    fieldType={FieldTypes.INPUT}
                                />
                                <InputGroup className="plain-control">
                                    <Form.Control
                                        className={`${methods?.formState?.errors?.email ? 'is-invalid' : ''}`}
                                        {...emailRegister}
                                        onBlur={(ev) => {
                                            ev.target.value = ev.target.value.trim();
                                            emailRegister?.onBlur(ev);
                                        }}
                                    />
                                </InputGroup>
                            </Form.Group>
                            <Button
                                className="submitButton w-100 mt-4"
                                type="submit"
                                disabled={methods.formState.isSubmitting}>
                                Continue
                            </Button>
                        </Form>
                    </FormProvider>
                </div>
            </div>
            {!isModal && (
                <div className="mt-4 text-center">
                    <Link legacyBehavior href="/dealer/signin" className="text-link">
                        <a>Dealer sign in</a>
                    </Link>
                </div>
            )}
        </div>
    );
};

export default SigninOptions;
